"use client";

// FILE: src/features/nmck-calculator/components/calculator-form.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT:
// PURPOSE: Форма расчёта Начальной (Максимальной) Цены Контракта (НМЦК) для охранных услуг по Приказу Росгвардии №45.
// SCOPE: Многошаговая форма (Контракт → Посты и Опции → Результат → Запрос КП у ЧОП) с подстановкой МРОТ по регионам, расчётом ночных/праздничных часов, коэффициентов усложнения, СТСО, страхования, инфляции и НДС. Экспорт в CSV/HTML/PDF, шаринг по ссылке, анализ коэффициента вариации цен поставщиков.
// INPUT: Параметры контракта (даты, регион, МРОТ, СНР, коэфф.), параметры постов (кол-во, часы, коэфф. усложнения), фин. параметры (ставки, СТСО, страхование), цены поставщиков.
// OUTPUT: CalculateNmtckResponse — полный расчёт НМЦК; CSV/HTML/PDF-отчёты; ссылка для шаринга; запрос КП в ЧОП.
// KEYWORDS: DOMAIN(NMCC): ContractCalculation; DOMAIN(Regulation): RosgvardiaOrder45; TECH(React): ClientComponent; MATH(CoefficientOfVariation); MATH(MROT)
// STATES: [loading: спиннер загрузки формы/регионов; empty: нет постов — предложить добавить; error: ошибка расчёта с сообщением; data: форма расчёта с результатами]
// END_MODULE_CONTRACT
//
// START_RATIONALE:
// Q: Почему расчёт НМЦК происходит на клиенте (React), а не на сервере?
// A: Чтобы дать пользователю мгновенную обратную связь при изменении параметров (MROT, U-коэффициенты, количество постов). Серверный RPC calculate-nmtck используется только для финального "живого" расчёта при отправке формы.
// Q: Почему используется динамический импорт (next/dynamic) для формы?
// A: Библиотеки расчёта (date-fns, zod) тяжелые — динамический импорт сокращает размер初始 бандла на ~40KB. Форма рендерится только при скролле пользователя к калькулятору.
// END_RATIONALE
//
// START_CHANGE_SUMMARY:
// LAST_CHANGE: 1.3.0 — Добавлен Step 4 «Запрос КП у ЧОП»: интеграция с рейтингом ЧОП, поиск/замена, отправка запросов.
// PREV_CHANGE_SUMMARY: 1.2.0 — Добавлены быстрые пресеты (ТЦ/Завод/Школа/Склад/Офис) + интеграция CostBreakdownBar (структура цены: stacked bar, легенда, мета-карточки, детальная разбивка).
// END_CHANGE_SUMMARY
//
// START_MODULE_MAP:
// FUNC [10] Главная форма-компонент с шагами => CalculatorForm
// FUNC [7] Сабмит и агрегация расчёта по postConfigs => onSubmit
// FUNC [4] Экспорт в CSV => handleExportCSV
// FUNC [5] Экспорт в HTML-отчёт => handleExportHTML
// FUNC [4] Экспорт в PDF через API => handleExportPDF
// FUNC [4] Шаринг расчёта по ссылке => handleShare
// FUNC [3] Расчёт коэффициента вариации цен => calculateVariation
// FUNC [2] Инлайн-тултип => InfoTooltip
// STATE [7] Массив конфигураций постов с индивидуальными U => postConfigs
// STATE [5] Массив результатов расчёта по каждому посту => perPostResults
// HOOK [6] Клиентский предварительный расчёт НМЦК => useNmtckPreview
// FUNC [5] Чистая функция предварительного расчёта => calculateNmtckPreview
// END_MODULE_MAP

import { useState, useEffect, useRef } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Loader2, Calculator, Info, Settings2, ShieldCheck, ShieldAlert, Sparkles, Download, Share2, ArrowRight, ArrowLeft, BarChart3 } from "lucide-react";

import { Button } from "@oxpa/shared/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@oxpa/shared/ui/form";
import { Input } from "@oxpa/shared/ui/input";
import { Checkbox } from "@oxpa/shared/ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@oxpa/shared/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@oxpa/shared/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@oxpa/shared/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@oxpa/shared/ui/tooltip";

import { calculateNmtckAction, CalculateNmtckResponse } from "../actions/calculate";
import { saveAndGetShareLink } from "../actions/share";
import { getNmckRegions, getNmckCalendars, NmckRegion, NmckCalendar } from "../actions/regions";
import { calculateContractHours } from "../lib/time-calculator";
import { calculateNmtckPreview } from "../hooks/use-nmtck-preview";
import { CostBreakdownBar } from "./cost-breakdown-bar";
import { ChopRequestStep } from "./chop-request-step";

const formSchema = z.object({
  title: z.string().optional(),
  start_date: z.string().min(10),
  end_date: z.string().min(10),
  region_id: z.string().min(1),
  
  // Custom overrides (visible in advanced)
  mrot_override: z.coerce.number().min(0).optional(),
  snr_override: z.coerce.number().min(0).optional(),
  regional_coef: z.coerce.number().min(1).max(3),
  north_coef: z.coerce.number().min(0).max(100),
  
  insurance_rate: z.coerce.number().min(0).max(1),
  indirect_rate: z.coerce.number().min(0).max(1),
  profit_rate: z.coerce.number().min(0).max(1),

  stso_amount: z.coerce.number().min(0),
  insurance_amount: z.coerce.number().min(0),

  inflation_index: z.coerce.number().min(1).max(2),
  vat_percent: z.coerce.number().min(0).max(22),
});

// START_PRESETS: Быстрые сценарии расчёта НМЦК
// Маппинг дизайн-прототипа (ТЦ/Завод/Школа/Склад/Офис) на реальную модель формы:
// posts_count + schedule_hours → postConfigs[0]; regionMatch → regions[].name;
// u-коэффициенты → подобраны по типу объекта (Приказ №45, Таблица №2).
interface NmckPreset {
  key: string;
  label: string;
  desc: string;
  posts_count: number;
  schedule_hours: number;
  regionMatch: string;
  u_special_eq: boolean;
  u_armed: boolean;
  u_mass_events: boolean;
  u_antiterror: boolean;
  u_state_secret: boolean;
}

const PRESETS: NmckPreset[] = [
  {
    key: "tc",
    label: "ТЦ",
    desc: "2 поста · 24ч · Москва",
    posts_count: 2,
    schedule_hours: 24,
    regionMatch: "Москва",
    u_special_eq: false,
    u_armed: false,
    u_mass_events: true,
    u_antiterror: false,
    u_state_secret: false,
  },
  {
    key: "zavod",
    label: "Завод",
    desc: "5 постов · 24ч · оружие",
    posts_count: 5,
    schedule_hours: 24,
    regionMatch: "Москва",
    u_special_eq: false,
    u_armed: true,
    u_mass_events: false,
    u_antiterror: false,
    u_state_secret: false,
  },
  {
    key: "school",
    label: "Школа",
    desc: "1 пост · 12ч · антитеррор",
    posts_count: 1,
    schedule_hours: 12,
    regionMatch: "Москва",
    u_special_eq: false,
    u_armed: false,
    u_mass_events: true,
    u_antiterror: true,
    u_state_secret: false,
  },
  {
    key: "sklad",
    label: "Склад",
    desc: "8 постов · 24ч · Казань",
    posts_count: 8,
    schedule_hours: 24,
    regionMatch: "Казань",
    u_special_eq: true,
    u_armed: true,
    u_mass_events: false,
    u_antiterror: false,
    u_state_secret: false,
  },
  {
    key: "office",
    label: "Офис",
    desc: "3 поста · 24ч · СПб",
    posts_count: 3,
    schedule_hours: 24,
    regionMatch: "Санкт-Петербург",
    u_special_eq: true,
    u_armed: false,
    u_mass_events: false,
    u_antiterror: false,
    u_state_secret: false,
  },
];
// END_PRESETS

export const CalculatorForm = () => {
  const formRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState("step1");
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [result, setResult] = useState<CalculateNmtckResponse | null>(null);
  const [supplierPrices, setSupplierPrices] = useState<number[]>([]);
  const [newSupplierPrice, setNewSupplierPrice] = useState<string>('');
  const [shareUrl, setShareUrl] = useState<string>('');
  const [shareLoading, setShareLoading] = useState(false);
  interface PostConfig {
    id: string;
    posts_count: number;
    schedule_hours: number;
    u_special_eq: boolean;
    u_armed: boolean;
    u_mass_events: boolean;
    u_antiterror: boolean;
    u_state_secret: boolean;
  }
  
  const [postConfigs, setPostConfigs] = useState<PostConfig[]>([{
    id: typeof crypto !== 'undefined' ? crypto.randomUUID() : '1',
    posts_count: 1,
    schedule_hours: 24,
    u_special_eq: false,
    u_armed: false,
    u_mass_events: false,
    u_antiterror: false,
    u_state_secret: false,
  }]);
  
  const [perPostResults, setPerPostResults] = useState<CalculateNmtckResponse[]>([]);
  
  const [regions, setRegions] = useState<NmckRegion[]>([]);
  const [calendars, setCalendars] = useState<NmckCalendar[]>([]);
  
  const defaultStartDate = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toISOString().split("T")[0]; // Next month 1st
  const defaultEndDate = new Date(new Date().getFullYear(), new Date().getMonth() + 2, 0).toISOString().split("T")[0]; // Next month last day

  type FormValues = z.infer<typeof formSchema>;
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema) as any,
    defaultValues: {
      title: "Охрана объекта (по Приказу №45)",
      start_date: defaultStartDate,
      end_date: defaultEndDate,
      region_id: "",
      regional_coef: 1.0,
      north_coef: 0,
      
      insurance_rate: 0.302, // 30.2%
      indirect_rate: 0.2, // 20%
      profit_rate: 0.05, // 5%

      stso_amount: 0,
      insurance_amount: 0,

      inflation_index: 1.0,
      vat_percent: 22, // Основная ставка НДС 22% (УСН = 5%, 7% по выбору)
    },
  });

  useEffect(() => {
    async function loadData() {
      const [regs, cals] = await Promise.all([getNmckRegions(), getNmckCalendars()]);
      setRegions(regs);
      setCalendars(cals);
      
      if (regs.length > 0) {
        // Auto-select first base region
        const base = regs.find(r => r.name.includes('Базовая')) || regs[0];
        form.setValue("region_id", base.id);
      }
    }
    loadData();
  }, [form]);

  // When region changes, update coefs
  const watchRegion = form.watch("region_id");
  useEffect(() => {
    const reg = regions.find(r => r.id === watchRegion);
    if (reg) {
      form.setValue("regional_coef", reg.regional_coef);
      form.setValue("north_coef", reg.north_coef);
    }
  }, [watchRegion, regions, form]);

  /* <MEMENTO>
  AI_REFLECTION: DEPTH STANDARD
  RATIONALE: applyPreset маппит дизайн-пресеты (ТЦ/Завод/Школа/Склад/Офис) на реальную модель формы. Сбрасывает postConfigs до одного поста с параметрами пресета, ищет регион по подстроке в name, переключает на шаг 2 и скроллит к форме. activePreset подсвечивает активную кнопку.
  CONTEXT_DEPENDENCIES: PRESETS, regions, postConfigs, form
  </MEMENTO> */
  function applyPreset(preset: NmckPreset) {
    setActivePreset(preset.key);
    setPostConfigs([{
      id: typeof crypto !== 'undefined' ? crypto.randomUUID() : '1',
      posts_count: preset.posts_count,
      schedule_hours: preset.schedule_hours,
      u_special_eq: preset.u_special_eq,
      u_armed: preset.u_armed,
      u_mass_events: preset.u_mass_events,
      u_antiterror: preset.u_antiterror,
      u_state_secret: preset.u_state_secret,
    }]);
    const match = regions.find(r =>
      r.name.toLowerCase().includes(preset.regionMatch.toLowerCase())
    );
    if (match) {
      form.setValue("region_id", match.id);
    }
    setActiveTab("step2");
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
    if (typeof window !== 'undefined' && (window as any).ym) {
      (window as any).ym(107253138, 'reachGoal', 'nmck_preset_' + preset.key);
    }
  }

  /* <MEMENTO>
  AI_REFLECTION: DEPTH STANDARD
  RATIONALE: onSubmit содержит обновлённый пайплайн расчёта НМЦК с индивидуальными U-коэффициентами для каждого поста. Итерация по postConfigs (вместо allGroups + extraGroups), взвешенное среднее для base_hour_rate и adjusted_hour_rate по total_hours. Сохраняет perPostResults для детализации.
  CONTEXT_DEPENDENCIES: calculateContractHours, calculateNmtckAction, postConfigs, formSchema
  </MEMENTO> */
  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      setIsPending(true);
      
      const startDate = new Date(values.start_date);
      const endDate = new Date(values.end_date);
      const year = startDate.getFullYear();
      let cal = calendars.find(c => c.year === year);
      
      if (!cal) {
        cal = { year, snr_hours: 164.33, holidays_dates: [] };
      }
      
      const region = regions.find(r => r.id === values.region_id);
      const mrot = values.mrot_override || (region ? region.mrot_value : 27093);
      const snr = values.snr_override || cal.snr_hours;

      /* <MEMENTO>
      AI_REFLECTION: DEPTH STANDARD
      RATIONALE: Итерация по postConfigs с индивидуальными U-коэффициентами. Каждый пост рассчитывается отдельно через RPC calculate_nmtck. Promise.all для параллельного выполнения — N round-trips вместо 1.
      </MEMENTO> */
      const results = await Promise.all(
        postConfigs.map(async (pc) => {
          const hd = calculateContractHours(startDate, endDate, pc.schedule_hours, cal.holidays_dates);
          const p = {
            mrot_value: mrot, regional_coef: values.regional_coef, north_coef: values.north_coef,
            snr_hours: snr,
            total_hours: hd.totalHours, night_hours: hd.nightHours, holiday_hours: hd.holidayHours,
            posts_count: pc.posts_count, schedule_hours: pc.schedule_hours,
            insurance_rate: values.insurance_rate, indirect_rate: values.indirect_rate,
            profit_rate: values.profit_rate,
            stso_amount: 0, insurance_amount: 0,
            u_special_eq: pc.u_special_eq, u_armed: pc.u_armed,
            u_mass_events: pc.u_mass_events, u_antiterror: pc.u_antiterror,
            u_state_secret: pc.u_state_secret,
            inflation_index: 1.0, vat_percent: 0,
            title: values.title
          };
          return calculateNmtckAction(p);
        })
      );

      let aggregated: CalculateNmtckResponse | null = null;
      let totalWeight = 0;
      let weightedBaseRate = 0;
      let weightedAdjustedRate = 0;

      for (const r of results) {
        if (!aggregated) {
          aggregated = { ...r };
          weightedBaseRate = r.base_hour_rate * r.total_hours;
          weightedAdjustedRate = r.adjusted_hour_rate * r.total_hours;
          totalWeight = r.total_hours;
        } else {
          aggregated.total_hours += r.total_hours;
          aggregated.night_addition_total += r.night_addition_total;
          aggregated.holiday_addition_total += r.holiday_addition_total;
          aggregated.vacation_reserve_total += r.vacation_reserve_total;
          aggregated.fot_total += r.fot_total;
          aggregated.sv_total += r.sv_total;
          aggregated.direct_cost_total += r.direct_cost_total;
          aggregated.indirect_cost_total += r.indirect_cost_total;
          aggregated.profit_total += r.profit_total;
          aggregated.posts_count += r.posts_count;
          aggregated.svohr_total += r.svohr_total;
          aggregated.nmtck_total += r.nmtck_total;
          aggregated.vat_amount += r.vat_amount;
          aggregated.total_u_coef = Math.max(aggregated.total_u_coef, r.total_u_coef);
          weightedBaseRate += r.base_hour_rate * r.total_hours;
          weightedAdjustedRate += r.adjusted_hour_rate * r.total_hours;
          totalWeight += r.total_hours;
        }
      }

      if (aggregated) {
        aggregated.base_hour_rate = totalWeight > 0 ? weightedBaseRate / totalWeight : 0;
        aggregated.adjusted_hour_rate = totalWeight > 0 ? weightedAdjustedRate / totalWeight : 0;
        
        const preInflation = aggregated.svohr_total + values.stso_amount + values.insurance_amount;
        const withInflation = preInflation * values.inflation_index;
        const vatAmount = withInflation * (values.vat_percent / 100);
        aggregated.nmtck_total = withInflation + vatAmount;
        aggregated.vat_amount = vatAmount;
      }

      setResult(aggregated);
      setPerPostResults(results);
      setIsPending(false);
      setActiveTab("step3");
      if (typeof window !== 'undefined' && (window as any).ym) {
        (window as any).ym(107253138, 'reachGoal', 'nmck_calculate');
      }
    } catch (error) {
      console.error("NMCC calculation failed:", error);
      setIsPending(false);
      alert("Ошибка расчёта. Попробуйте снова.");
    }
  }

  /* <MEMENTO>
  AI_REFLECTION: DEPTH LITE
  RATIONALE: CSV-экспорт с BOM (\uFEFF) для корректного отображения кириллицы в Excel. Создание/удаление <a>-элемента через DOM, отзыв ObjectURL через setTimeout. При рефакторинге не менять порядок полей — он соответствует Приказу №45.
  CONTEXT_DEPENDENCIES: result, Yandex.Metrika goal
  </MEMENTO> */
  const handleExportCSV = () => {
    if (!result) return;
    const csvContent = 
      "Параметр,Значение,Единица\n" +
      `Количество постов (суммарно),${result.posts_count},шт\n` +
      `Итого часов по контракту,${result.total_hours},ч\n` +
      `Итоговый коэффициент сложности (U),${result.total_u_coef},\n` +
      `Базовая ставка ЗП в час,${(result.base_hour_rate || 0).toFixed(2)},руб\n` +
      `Скорректированная ставка за 1 час,${(result.adjusted_hour_rate || 0).toFixed(2)},руб\n` +
      `Доплата за ночное время (20%),${(result.night_addition_total || 0).toFixed(2)},руб\n` +
      `Доплата за праздничные (100%),${(result.holiday_addition_total || 0).toFixed(2)},руб\n` +
      `Резерв на оплату отпусков,${(result.vacation_reserve_total || 0).toFixed(2)},руб\n` +
      `ФОТ за период,${(result.fot_total || 0).toFixed(2)},руб\n` +
      `Страховые взносы (СВ),${(result.sv_total || 0).toFixed(2)},руб\n` +
      `Прямые затраты (ФОТ + СВ),${(result.direct_cost_total || 0).toFixed(2)},руб\n` +
      `Косвенные расходы (20%),${(result.indirect_cost_total || 0).toFixed(2)},руб\n` +
      `Прибыль (5%),${(result.profit_total || 0).toFixed(2)},руб\n` +
      `СВОХР (Стоимость охраны),${(result.svohr_total || 0).toFixed(2)},руб\n` +
      `НДС / Налог,${(result.vat_amount || 0).toFixed(2)},руб\n` +
      `ИТОГО НМЦК,${(result.nmtck_total || 0).toFixed(2)},руб\n`;
      
    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "nmck_calculation_2026.csv";
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
    if (typeof window !== 'undefined' && (window as any).ym) {
      (window as any).ym(107253138, 'reachGoal', 'nmck_export_csv');
    }
  };

  /* <MEMENTO>
  AI_REFLECTION: DEPTH LITE
  RATIONALE: Коэффициент вариации (CV) рассчитывается по формуле std/mean*100. Порог однородности 15% зафиксирован п.9 Приказа №45. Выборка < 3 → null. Исправлено: стандартное отклонение по выборке (n-1), а не по генеральной совокупности.
  CONTEXT_DEPENDENCIES: supplierPrices, п.9 Приказа №45
  </MEMENTO> */
  const calculateVariation = (prices: number[]): { cv: number; mean: number; homogeneous: boolean } | null => {
    if (prices.length < 3) return null;
    const n = prices.length;
    const mean = prices.reduce((a, b) => a + b, 0) / n;
    const squaredDiffs = prices.map(p => Math.pow(p - mean, 2));
    const stdDev = Math.sqrt(squaredDiffs.reduce((a, b) => a + b, 0) / (n - 1));
    const cv = (stdDev / mean) * 100;
    return { cv, mean, homogeneous: cv <= 15 };
  };

  /* <MEMENTO>
  AI_REFLECTION: DEPTH LITE
  RATIONALE: Генерация HTML-отчёта инлайн-строкой без шаблонизатора. Стили встроены в <style> для самодостаточности файла. Формат соответствует требованиям ЕИС к обоснованию НМЦК. При изменении полей синхронизировать с handleExportCSV.
  CONTEXT_DEPENDENCIES: result
  </MEMENTO> */
  const handleExportHTML = () => {
    if (!result) return;
    
    // Формируем красивый HTML отчет
    const html = `<!DOCTYPE html>
<html lang="ru">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Обоснование НМЦК (Охрана)</title>
    <style>
        body { font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; background: #0a0a0a; color: #f3f4f6; padding: 3rem 1rem; line-height: 1.6; }
        .container { max-width: 800px; margin: 0 auto; background: #111; padding: 2rem; border: 1px solid #333; border-radius: 8px; box-shadow: 0 10px 30px rgba(0,0,0,0.5); }
        .header { border-bottom: 1px solid #333; padding-bottom: 1.5rem; margin-bottom: 2rem; text-align: center; }
        .header h1 { font-size: 1.5rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; margin: 0; color: #fff; }
        .header p { color: #888; font-size: 0.9rem; margin-top: 0.5rem; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 2rem; font-family: monospace; font-size: 15px; }
        th, td { padding: 1rem; text-align: left; border-bottom: 1px solid #222; }
        th { color: #888; font-weight: normal; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.1em; width: 60%; }
        td { color: #ddd; }
        td.amount { text-align: right; font-weight: bold; }
        .total-box { background: #1a1a1a; border: 1px solid #444; padding: 2rem; text-align: center; border-radius: 4px; margin-top: 2rem; }
        .total-label { font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.2em; color: #888; margin-bottom: 0.5rem; }
        .total-value { font-size: 3rem; font-weight: 300; color: #fff; line-height: 1; }
        .footer { margin-top: 3rem; text-align: center; font-size: 0.8rem; color: #555; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Обоснование НМЦК</h1>
            <p>в соответствии с Приказом Росгвардии от 15.02.2021 № 45 (ред. 2026г)</p>
        </div>
        
        <table>
            <thead>
                <tr>
                    <th>Наименование показателя</th>
                    <th style="text-align: right;">Значение</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>Количество круглосуточных постов (эквивалент)</td>
                    <td class="amount">${result.posts_count} шт.</td>
                </tr>
                <tr>
                    <td>Итого часов охраны по контракту (включая ночные и праздничные)</td>
                    <td class="amount">${result.total_hours.toLocaleString('ru-RU')} ч.</td>
                </tr>
                <tr>
                    <td>Итоговый коэффициент сложности (U)</td>
                    <td class="amount">${result.total_u_coef.toFixed(2)}</td>
                </tr>
                <tr>
                    <td>Базовая ставка заработной платы за 1 час рабочего времени</td>
                    <td class="amount">${(result.base_hour_rate || 0).toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</td>
                </tr>
                <tr>
                    <td>Скорректированная ставка за 1 час (с учетом районных и северных надбавок)</td>
                    <td class="amount">${(result.adjusted_hour_rate || 0).toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</td>
                </tr>
                <tr>
                    <td>Доплата за работу в ночное время (20%) за период контракта</td>
                    <td class="amount">${(result.night_addition_total || 0).toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</td>
                </tr>
                <tr>
                    <td>Доплата за работу в нерабочие праздничные дни (100%) за период контракта</td>
                    <td class="amount">${(result.holiday_addition_total || 0).toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</td>
                </tr>
                <tr>
                    <td>Резерв на оплату отпусков работников</td>
                    <td class="amount">${(result.vacation_reserve_total || 0).toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</td>
                </tr>
                <tr style="background: #222;">
                    <td><strong>Фонд оплаты труда (ФОТ) охранников за период</strong></td>
                    <td class="amount"><strong>${result.fot_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</strong></td>
                </tr>
                <tr>
                    <td>Страховые взносы на обязательное социальное страхование (СВ)</td>
                    <td class="amount">${result.sv_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</td>
                </tr>
                <tr style="background: #222;">
                    <td><strong>Рп (Прямые затраты) - ФОТ с учетом СВ и коэффициента U</strong></td>
                    <td class="amount"><strong>${result.direct_cost_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</strong></td>
                </tr>
                <tr>
                    <td>Накладные расходы (Косвенные расходы) — 20%</td>
                    <td class="amount">${result.indirect_cost_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</td>
                </tr>
                <tr>
                    <td>Прибыль — 5%</td>
                    <td class="amount">${result.profit_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</td>
                </tr>
                <tr>
                    <td>СВОХР (Стоимость охраны до налогов)</td>
                    <td class="amount">${result.svohr_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</td>
                </tr>
                <tr>
                    <td>Сумма НДС / Налога</td>
                    <td class="amount">${result.vat_amount.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</td>
                </tr>
            </tbody>
        </table>

        <div class="total-box">
            <div class="total-label">Итоговая Начальная (максимальная) цена контракта</div>
            <div class="total-value">${result.nmtck_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</div>
        </div>
        
        <div class="footer">
            Документ сформирован автоматически системой управления OXPA.PRO<br>
            Дата формирования: ${new Date().toLocaleDateString('ru-RU')}
        </div>
    </div>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `nmck_report_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
    if (typeof window !== 'undefined' && (window as any).ym) {
      (window as any).ym(107253138, 'reachGoal', 'nmck_export_html');
    }
  };

  /* <MEMENTO>
  AI_REFLECTION: DEPTH LITE
  RATIONALE: PDF-генерация через POST /api/nmck/pdf. Если API упадёт (нет соединения, таймаут), показываем alert. Blob скачивается через динамический <a>. При изменении структуры result синхронизировать API-эндпоинт.
  CONTEXT_DEPENDENCIES: result, /api/nmck/pdf
  </MEMENTO> */
  const handleExportPDF = async () => {
    if (!result) return;
    try {
      const res = await fetch("/api/nmck/pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: "Обоснование НМЦК", result }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "NMTSK_obosnovanie.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      if (typeof window !== 'undefined' && (window as any).ym) {
        (window as any).ym(107253138, 'reachGoal', 'nmck_export_pdf');
      }
    } catch (e: any) {
      console.error("PDF export error:", e);
      alert("Ошибка генерации PDF. Попробуйте снова.");
    }
  };

  /* <MEMENTO>
  AI_REFLECTION: DEPTH LITE
  RATIONALE: Сохранение расчёта через saveAndGetShareLink, копирование ссылки в буфер. Yandex.Metrika goal. Использует U-коэффициенты из первого postConfig.
  CONTEXT_DEPENDENCIES: result, saveAndGetShareLink, postConfigs
  </MEMENTO> */
  const handleShare = async () => {
    if (!result) return;
    setShareLoading(true);
    try {
      const values = form.getValues();
      const region = regions.find(r => r.id === values.region_id);
      const mrot = values.mrot_override || (region ? region.mrot_value : 27093);
      const year = new Date(values.start_date).getFullYear();
      const calendar = calendars.find(c => c.year === year);
      const snr = values.snr_override || calendar?.snr_hours || 164.33;
      
      const pc = postConfigs[0];
      const hd = pc ? calculateContractHours(
        new Date(values.start_date), new Date(values.end_date),
        pc.schedule_hours, calendar?.holidays_dates || []
      ) : { totalHours: 0, nightHours: 0, holidayHours: 0, daysCount: 0 };
      
      const id = await saveAndGetShareLink(
        {
          mrot_value: mrot,
          regional_coef: values.regional_coef,
          north_coef: values.north_coef,
          snr_hours: snr,
          total_hours: result.total_hours,
          night_hours: hd.nightHours,
          holiday_hours: hd.holidayHours,
          posts_count: result.posts_count,
          schedule_hours: pc?.schedule_hours || 24,
          insurance_rate: values.insurance_rate,
          indirect_rate: values.indirect_rate,
          profit_rate: values.profit_rate,
          stso_amount: values.stso_amount,
          insurance_amount: values.insurance_amount,
          u_special_eq: pc?.u_special_eq || false,
          u_armed: pc?.u_armed || false,
          u_mass_events: pc?.u_mass_events || false,
          u_antiterror: pc?.u_antiterror || false,
          u_state_secret: pc?.u_state_secret || false,
          inflation_index: values.inflation_index,
          vat_percent: values.vat_percent,
          title: values.title || "Расчет НМЦК",
        },
        result
      );
      const url = `${window.location.origin}/calc/s/${id}`;
      setShareUrl(url);
      await navigator.clipboard.writeText(url);
      if (typeof window !== 'undefined' && (window as any).ym) {
        (window as any).ym(107253138, 'reachGoal', 'nmck_share');
      }
      alert("Ссылка скопирована! Отправьте её заказчику или коллеге.");
    } catch (e: any) {
      console.error("Share error:", e);
      alert("Не удалось сохранить расчёт. Попробуйте снова.");
    } finally {
      setShareLoading(false);
    }
  };

  const InfoTooltip = ({ text }: { text: string }) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-4 w-4 text-white/40 hover:text-white transition-colors cursor-help inline ml-2" />
        </TooltipTrigger>
        <TooltipContent className="bg-white text-black p-3 max-w-xs text-xs font-mono">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <div ref={formRef} className="w-full max-w-5xl mx-auto scroll-mt-20">
      {/* START_PRESETS_UI: Быстрые сценарии расчёта */}
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 mb-3.5 font-mono">
          Быстрый сценарий — нажмите, чтобы подставить параметры
        </div>
        <div className="flex flex-wrap gap-2.5">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => applyPreset(p)}
              className={
                "flex flex-col items-start gap-1 text-left px-4 py-3 border-2 transition-all font-mono font-semibold text-[13px] uppercase tracking-[0.04em] " +
                (activePreset === p.key
                  ? "border-green-500 bg-green-500/10 text-green-400"
                  : "border-white/20 bg-white/5 text-white hover:border-green-500 hover:bg-green-500/5")
              }
            >
              {p.label}
              <small className="font-sans font-medium text-[11px] normal-case tracking-normal text-white/50">
                {p.desc}
              </small>
            </button>
          ))}
        </div>
      </div>
      {/* END_PRESETS_UI */}
      <Form {...form}>
        {/* BUG_FIX_CONTEXT: onError handler обязателен с e.preventDefault() для подавления нативного form submit при ошибках валидации */}
        <form onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit(onSubmit as any, (errors) => {
            if (errors.region_id) setActiveTab("step1");
          })(e);
        }} className="space-y-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4 bg-white/5 border border-white/10 rounded-none h-14 p-1">
              <TabsTrigger value="step1" className="rounded-none uppercase tracking-widest text-[10px] data-[state=active]:bg-white data-[state=active]:text-black">1. Контракт</TabsTrigger>
              <TabsTrigger value="step2" className="rounded-none uppercase tracking-widest text-[10px] data-[state=active]:bg-white data-[state=active]:text-black">2. Посты</TabsTrigger>
              <TabsTrigger value="step3" className="rounded-none uppercase tracking-widest text-[10px] data-[state=active]:bg-white data-[state=active]:text-black" disabled={!result}>3. Результат</TabsTrigger>
              <TabsTrigger value="step4" className="rounded-none uppercase tracking-widest text-[10px] data-[state=active]:bg-white data-[state=active]:text-black" disabled={!result}>4. Запрос КП</TabsTrigger>
            </TabsList>
            
            {/* ШАГ 1: Контракт */}
            <TabsContent value="step1" className="bg-black border border-white/10 p-6 mt-4 animate-in fade-in-50">
              <div className="space-y-6">
                <div className="flex items-center gap-3 border-b border-white/10 pb-4">
                  <Calculator className="h-5 w-5 text-white" />
                  <h3 className="text-sm uppercase tracking-widest text-white font-bold">Базовые параметры контракта</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField control={form.control as any} name="title" render={({ field }) => (
                    <FormItem className="col-span-1 md:col-span-2">
                      <FormLabel className="text-[10px] uppercase tracking-widest text-white/50">Название закупки</FormLabel>
                      <FormControl>
                        <Input className="rounded-none bg-white/5 border-white/20 text-white font-mono h-12" {...field} />
                      </FormControl>
                    </FormItem>
                  )} />

                  <FormField control={form.control as any} name="start_date" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[10px] uppercase tracking-widest text-white/50">
                        Дата начала
                        <InfoTooltip text="Для точного расчета праздничных и ночных часов, попадающих в этот период." />
                      </FormLabel>
                      <FormControl>
                        <Input type="date" className="rounded-none bg-white/5 border-white/20 text-white font-mono h-12" {...field} />
                      </FormControl>
                    </FormItem>
                  )} />
                  
                  <FormField control={form.control as any} name="end_date" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[10px] uppercase tracking-widest text-white/50">Дата окончания</FormLabel>
                      <FormControl>
                        <Input type="date" className="rounded-none bg-white/5 border-white/20 text-white font-mono h-12" {...field} />
                      </FormControl>
                    </FormItem>
                  )} />

                  <FormField control={form.control as any} name="region_id" render={({ field }) => (
                    <FormItem className="col-span-1 md:col-span-2">
                      <FormLabel className="text-[10px] uppercase tracking-widest text-white/50">Регион (подстановка МРОТ)</FormLabel>
                      {regions.length > 0 ? (
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="rounded-none bg-white/5 border-white/20 text-white font-mono h-12">
                              <SelectValue placeholder="Выберите регион" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-black border-white/20 text-white">
                            {regions.map(r => (
                              <SelectItem key={r.id} value={r.id}>{r.name} (МРОТ: {r.mrot_value} ₽)</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="h-12 flex items-center bg-white/5 text-white/40 text-xs px-4 animate-pulse">Загрузка регионов...</div>
                      )}
                    </FormItem>
                  )} />

                  <div className="col-span-1 md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <FormField control={form.control as any} name="regional_coef" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[10px] uppercase tracking-widest text-white/50">Рай. Коэфф.</FormLabel>
                        <FormControl><Input type="number" step="0.1" className="rounded-none bg-white/5 border-white/20 text-white font-mono h-10" {...field} /></FormControl>
                      </FormItem>
                    )} />
                    <FormField control={form.control as any} name="north_coef" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[10px] uppercase tracking-widest text-white/50">Северная надб. (%)</FormLabel>
                        <FormControl><Input type="number" className="rounded-none bg-white/5 border-white/20 text-white font-mono h-10" {...field} /></FormControl>
                      </FormItem>
                    )} />
                    <FormField control={form.control as any} name="inflation_index" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[10px] uppercase tracking-widest text-white/50">Индекс инфляции</FormLabel>
                        <FormControl><Input type="number" step="0.01" className="rounded-none bg-white/5 border-white/20 text-white font-mono h-10" {...field} /></FormControl>
                      </FormItem>
                    )} />
                    <FormField control={form.control as any} name="vat_percent" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[10px] uppercase tracking-widest text-white/50">НДС (%)</FormLabel>
                        <FormControl><Input type="number" className="rounded-none bg-white/5 border-white/20 text-white font-mono h-10" {...field} /></FormControl>
                      </FormItem>
                    )} />
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <Button type="button" onClick={async () => {
                    const valid = await form.trigger(["region_id", "start_date", "end_date"]);
                    if (valid) setActiveTab("step2");
                  }} className="rounded-none bg-white text-black hover:bg-white/80 uppercase tracking-widest text-[10px] h-12 px-8 font-bold">
                    Далее: Настройка Постов <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* ШАГ 2: Посты */}
            <TabsContent value="step2" className="bg-black border border-white/10 p-6 mt-4 animate-in fade-in-50">
              <div className="space-y-6">
                {/* Посты с индивидуальными настройками */}
                <div className="flex items-center gap-3 border-b border-white/10 pb-4">
                  <Settings2 className="h-5 w-5 text-white" />
                  <h3 className="text-sm uppercase tracking-widest text-white font-bold">Настройка постов и коэффициентов</h3>
                </div>

                {postConfigs.map((pc, i) => (
                  <div key={pc.id} className="border border-white/10 bg-white/5 p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-[10px] uppercase tracking-widest text-white font-bold">
                        Пост {i + 1}{i === 0 ? ' (основной)' : ''}
                      </h4>
                      {i > 0 && (
                        <Button type="button" variant="outline"
                          onClick={() => setPostConfigs(prev => prev.filter((_, j) => j !== i))}
                          className="rounded-none border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 px-3 uppercase tracking-widest text-[10px]">
                          Удалить
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">Постов</label>
                        <Input type="number" min={1} max={500} value={pc.posts_count}
                          onChange={e => setPostConfigs(prev => prev.map((x, j) => j === i ? { ...x, posts_count: parseInt(e.target.value) || 1 } : x))}
                          className="rounded-none bg-white/5 border-white/20 text-white font-mono h-10" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">Часов/сутки</label>
                        <Input type="number" min={1} max={24} value={pc.schedule_hours}
                          onChange={e => setPostConfigs(prev => prev.map((x, j) => j === i ? { ...x, schedule_hours: parseInt(e.target.value) || 24 } : x))}
                          className="rounded-none bg-white/5 border-white/20 text-white font-mono h-10" />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <h5 className="text-[10px] uppercase tracking-widest text-white/60 flex items-center">
                        <ShieldCheck className="mr-2 h-4 w-4" /> Коэффициенты усложнения (Uд)
                        <InfoTooltip text="Приказ №45, Таблица №2: Uд1=0.05 (спецсредства), Uд2=0.20 (оружие), Uд3=0.30 (массовые мероприятия), Uд4=0.10 (антитеррор), Uд5=0.05 (гостайна)." />
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {([
                          { key: 'u_special_eq' as const, label: 'Спецсредства (+0.05)' },
                          { key: 'u_armed' as const, label: 'Служебное оружие (+0.20)' },
                          { key: 'u_mass_events' as const, label: 'Массовые мероприятия (+0.30)' },
                          { key: 'u_antiterror' as const, label: 'Антитеррор. паспорт (+0.10)' },
                          { key: 'u_state_secret' as const, label: 'Гостайна (+0.05)' },
                        ]).map(({ key, label }) => (
                          <label key={key} className="flex items-center space-x-3 p-2 bg-black border border-white/10 cursor-pointer">
                            <Checkbox className="border-white/20 data-[state=checked]:bg-white"
                              checked={pc[key]}
                              onCheckedChange={(val) => setPostConfigs(prev => prev.map((x, j) => j === i ? { ...x, [key]: !!val } : x))} />
                            <span className="text-xs text-white/80 font-mono">{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}

                {/* BUG_FIX_CONTEXT: e.preventDefault() + stopPropagation обязательны — Button type="button" не всегда блокирует form submit в Next.js/React 18 */}
                <Button type="button" variant="outline"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPostConfigs(prev => [...prev, {
                      id: typeof crypto !== 'undefined' ? crypto.randomUUID() : String(Date.now()),
                      posts_count: 1, schedule_hours: 24,
                      u_special_eq: false, u_armed: false, u_mass_events: false,
                      u_antiterror: false, u_state_secret: false,
                    }]);
                  }}
                  className="rounded-none border-dashed border-white/20 text-white/50 hover:text-white hover:border-white/40 h-10 w-full uppercase tracking-widest text-[10px]"
                  aria-label="Добавить пост">
                  + Добавить пост
                </Button>

                <div className="grid grid-cols-2 gap-6 pt-2">
                  <FormField control={form.control as any} name="stso_amount" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[10px] uppercase tracking-widest text-white/50">Стоимость СТСО (руб) <InfoTooltip text="Технические средства охраны (монтаж, обслуживание) за весь период" /></FormLabel>
                      <FormControl><Input type="number" className="rounded-none bg-white/5 border-white/20 text-white font-mono h-12" {...field} /></FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control as any} name="insurance_amount" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[10px] uppercase tracking-widest text-white/50">Страхование жизни (руб) <InfoTooltip text="Дополнительное страхование (если предусмотрено ТЗ)" /></FormLabel>
                      <FormControl><Input type="number" className="rounded-none bg-white/5 border-white/20 text-white font-mono h-12" {...field} /></FormControl>
                    </FormItem>
                  )} />
                </div>

                {/* 📐 Предварительный расчёт (сворачиваемый) */}
                {(() => {
                  const pc = postConfigs[0];
                  if (!pc) return null;
                  const startDateVal = form.watch("start_date");
                  const endDateVal = form.watch("end_date");
                  if (!startDateVal || !endDateVal) return null;
                  // BUG_FIX_CONTEXT: Используем реальный календарь для holidayHours (было пустым массивом — январь показывал 0 праздничных)
                  const previewYear = new Date(startDateVal).getFullYear();
                  const previewCal = calendars.find(c => c.year === previewYear);
                  const hd = calculateContractHours(new Date(startDateVal), new Date(endDateVal), pc.schedule_hours, previewCal?.holidays_dates || []);
                  const region = regions.find(r => r.id === form.watch("region_id"));
                  const mrot = form.watch("mrot_override") || (region ? region.mrot_value : 27093);
                  const snrVal = form.watch("snr_override") || previewCal?.snr_hours || 164.33;
                  // BUG_FIX_CONTEXT: client uBase синхронизирован с RPC (>=3 guard, было pc.schedule_hours < 3 → ~1.96 вместо 1.0)
                  const uBase = pc.schedule_hours >= 24 ? 1.0 : pc.schedule_hours === 12 ? 1.5 : pc.schedule_hours >= 3 ? Math.max(0.5, 2 - 0.0417 * pc.schedule_hours) : 1.0;
                  const uDopSum = (pc.u_special_eq ? 0.05 : 0) + (pc.u_armed ? 0.20 : 0) + (pc.u_mass_events ? 0.30 : 0) + (pc.u_antiterror ? 0.10 : 0) + (pc.u_state_secret ? 0.05 : 0);
                  const preview = calculateNmtckPreview({
                    mrot, snr: snrVal,
                    regionalCoef: form.watch("regional_coef") || 1,
                    northCoef: form.watch("north_coef") || 0,
                    totalHours: hd.totalHours,
                    nightHours: hd.nightHours,
                    holidayHours: hd.holidayHours,
                    uBase, uDopSum,
                    insuranceRate: form.watch("insurance_rate") || 0.302,
                    indirectRate: form.watch("indirect_rate") || 0.2,
                    profitRate: form.watch("profit_rate") || 0.05,
                    postsCount: pc.posts_count,
                    stsoAmount: form.watch("stso_amount") || 0,
                    insuranceAmount: form.watch("insurance_amount") || 0,
                    inflationIndex: form.watch("inflation_index") || 1,
                    vatPercent: form.watch("vat_percent") || 22,
                  });
                  if (!preview) return null;
                  const lines = [
                    { label: 'БЗП = МРОТ / СНР', formula: `${mrot.toLocaleString('ru-RU')} / ${snrVal}`, value: preview.bzp.toFixed(2) + ' ₽/час' },
                    { label: 'Ставка с учётом РК', formula: `${preview.bzp.toFixed(2)} × ${(form.watch("regional_coef") || 1).toFixed(1)} × (1 + ${(form.watch("north_coef") || 0)}/100)`, value: preview.adjustedHour.toFixed(2) + ' ₽/час' },
                    { label: 'Доплата ночные (20%)', formula: `${preview.adjustedHour.toFixed(2)} × 0.20 × (${hd.nightHours}/${hd.totalHours})`, value: preview.nightAdditionPerHour.toFixed(2) + ' ₽/час' },
                    { label: 'Доплата праздничные (100%)', formula: `${preview.adjustedHour.toFixed(2)} × 1.00 × (${hd.holidayHours}/${hd.totalHours})`, value: preview.holidayAdditionPerHour.toFixed(2) + ' ₽/час' },
                    { label: 'Резерв отпусков (1/12)', formula: `(${preview.adjustedHour.toFixed(2)} + ${preview.nightAdditionPerHour.toFixed(2)} + ${preview.holidayAdditionPerHour.toFixed(2)}) / 12`, value: preview.vacationReservePerHour.toFixed(2) + ' ₽/час' },
                    { label: 'ФОТ за час', formula: `${preview.adjustedHour.toFixed(2)} + ${preview.nightAdditionPerHour.toFixed(2)} + ${preview.holidayAdditionPerHour.toFixed(2)} + ${preview.vacationReservePerHour.toFixed(2)}`, value: preview.fotPerHour.toFixed(2) + ' ₽/час' },
                    { label: 'Страховые взносы (30.2%)', formula: `${preview.fotPerHour.toFixed(2)} × 0.302`, value: preview.svPerHour.toFixed(2) + ' ₽/час' },
                    { label: 'Коэффициент U', formula: `${uBase.toFixed(2)} (база) + ${uDopSum.toFixed(2)} (доп)`, value: preview.uTotal.toFixed(2) },
                    { label: 'Прямые затраты за час', formula: `(${preview.fotPerHour.toFixed(2)} + ${preview.svPerHour.toFixed(2)}) × ${preview.uTotal.toFixed(2)}`, value: preview.directPerHour.toFixed(2) + ' ₽/час' },
                    { label: 'Накладные (20%)', formula: `${preview.directPerHour.toFixed(2)} × 0.20`, value: preview.indirectPerHour.toFixed(2) + ' ₽/час' },
                    { label: 'Прибыль (5%)', formula: `(${preview.directPerHour.toFixed(2)} + ${preview.indirectPerHour.toFixed(2)}) × 0.05`, value: preview.profitPerHour.toFixed(2) + ' ₽/час' },
                    { label: 'ФОТ за период, все посты', formula: `${preview.fotPerHour.toFixed(2)} × ${hd.totalHours} × ${pc.posts_count}`, value: preview.fotTotal.toLocaleString('ru-RU', {minimumFractionDigits: 2}) + ' ₽' },
                    { label: 'СВОХР за период, все посты', formula: `${preview.svohrPerHour.toFixed(2)} × ${hd.totalHours} × ${pc.posts_count}`, value: preview.svohrTotal.toLocaleString('ru-RU', {minimumFractionDigits: 2}) + ' ₽' },
                  ];
                  return (
                    <details className="group border border-white/10 bg-white/5">
                      <summary className="cursor-pointer list-none p-4 outline-none hover:bg-white/10 transition-colors flex justify-between items-center text-[10px] uppercase tracking-widest text-white">
                        <span className="flex items-center">
                          <Sparkles className="mr-2 h-4 w-4" /> Предварительный расчёт
                        </span>
                        <span className="transition-transform duration-300 group-open:rotate-180">
                          <svg fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="16">
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </span>
                      </summary>
                      <div className="p-4 border-t border-white/10">
                        <div className="text-xs font-mono space-y-1">
                          {lines.map((l, i) => (
                            <div key={i} className="grid grid-cols-12 gap-2 py-1 border-b border-white/5 last:border-0">
                              <span className="col-span-4 text-white/60 truncate">{l.label}</span>
                              <span className="col-span-5 text-white/40 truncate text-[10px]">{l.formula}</span>
                              <span className="col-span-3 text-right text-white font-bold">{l.value}</span>
                            </div>
                          ))}
                          <div className="grid grid-cols-12 gap-2 py-2 mt-2 border-t border-white/20 font-bold">
                            <span className="col-span-4 text-white">Предв. НМЦК (без НДС)</span>
                            <span className="col-span-5 text-white/40 text-[10px]">{preview.svohrTotal.toLocaleString('ru-RU')} × {form.watch("inflation_index") || 1}</span>
                            <span className="col-span-3 text-right text-green-500">{preview.nmtckPreVat.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</span>
                          </div>
                          <div className="grid grid-cols-12 gap-2 py-1">
                            <span className="col-span-4 text-white">НДС ({(form.watch("vat_percent") || 22)}%)</span>
                            <span className="col-span-5 text-white/40 text-[10px]">{preview.nmtckPreVat.toLocaleString('ru-RU')} × {(form.watch("vat_percent") || 22) / 100}</span>
                            <span className="col-span-3 text-right text-white">{preview.vatAmount.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</span>
                          </div>
                          <div className="grid grid-cols-12 gap-2 py-1 font-bold text-sm">
                            <span className="col-span-7 text-white">Предв. НМЦК итого</span>
                            <span className="col-span-5 text-right text-green-400">{preview.nmtckTotal.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</span>
                          </div>
                        </div>
                      </div>
                    </details>
                  );
                })()}

                <div className="flex justify-between pt-4">
                  <Button type="button" variant="outline" onClick={() => setActiveTab("step1")} className="rounded-none border-white/20 bg-transparent text-white hover:bg-white/10 h-10 w-10 p-0">
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <Button type="submit" className="rounded-none bg-white text-black hover:bg-white/80 uppercase tracking-widest text-[10px] h-12 px-8 font-bold ml-auto" disabled={isPending}>
                    {isPending ? <Loader2 className="mr-3 h-4 w-4 animate-spin" /> : <Sparkles className="mr-3 h-4 w-4" />}
                    {isPending ? "ВЫЧИСЛЕНИЕ..." : "РАССЧИТАТЬ НМЦК"}
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* ШАГ 3: Результат */}
            <TabsContent value="step3" className="bg-black border border-white/10 p-6 mt-4 animate-in fade-in-50">
              {result ? (
                <div className="space-y-6">
                  <div className="p-6 bg-white/5 border border-white/10 group">
                    <div className="text-[10px] font-bold text-white border border-white/20 bg-black uppercase tracking-[0.2em] mb-4 inline-block px-3 py-1">
                      ОБОСНОВАНИЕ СФОРМИРОВАНО
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">
                      Итоговая Начальная (максимальная) цена контракта (НМЦК)
                    </div>
                    <div className="text-4xl md:text-5xl font-light text-white tracking-tight font-mono break-all py-2">
                      {result.nmtck_total.toLocaleString('ru-RU', {minimumFractionDigits: 2, maximumFractionDigits: 2})} ₽
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 border border-white/10 bg-black">
                      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Часов всего</div>
                      <div className="text-xl font-mono text-white">{result.total_hours.toLocaleString('ru-RU')} ч.</div>
                    </div>
                    <div className="p-4 border border-white/10 bg-black">
                      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Коэфф U.</div>
                      <div className="text-xl font-mono text-white">{result.total_u_coef.toFixed(2)}</div>
                    </div>
                    <div className="p-4 border border-white/10 bg-black">
                      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Прибыль (5%)</div>
                      <div className="text-xl font-mono text-white">{result.profit_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</div>
                    </div>
                    <div className="p-4 border border-white/10 bg-black">
                      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">ФОТ (Зарплата)</div>
                      <div className="text-xl font-mono text-white">{result.fot_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</div>
                    </div>
                  </div>

                  {/* Структура цены — stacked bar + легенда + мета + детальная разбивка */}
                  <CostBreakdownBar result={result} />

                  {perPostResults.length > 1 && (
                    <Accordion type="single" collapsible className="border border-white/10 bg-white/5">
                      <AccordionItem value="breakdown">
                        <AccordionTrigger className="px-4 py-3 text-[10px] uppercase tracking-widest text-white hover:no-underline">
                          Детализация по постам ({perPostResults.length} постов)
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                          <div className="space-y-4">
                            {perPostResults.map((r, i) => (
                              <div key={i} className="border border-white/10 bg-black p-3">
                                <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Пост {i + 1}</div>
                                <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                                  <div><span className="text-white/40">Часов: </span><span className="text-white">{r.total_hours.toLocaleString('ru-RU')}</span></div>
                                  <div><span className="text-white/40">Коэфф U: </span><span className="text-white">{r.total_u_coef.toFixed(2)}</span></div>
                                  <div><span className="text-white/40">ФОТ: </span><span className="text-white">{r.fot_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</span></div>
                                  <div><span className="text-white/40">СВ: </span><span className="text-white">{r.sv_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</span></div>
                                  <div><span className="text-white/40">Прямые затраты: </span><span className="text-white">{r.direct_cost_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</span></div>
                                  <div><span className="text-white/40">СВОХР: </span><span className="text-white">{r.svohr_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</span></div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  )}
                  
                  {/* Коэффициент вариации (п.9 Приказа №45) */}
                  <div className="border border-white/10 bg-white/5 p-4 space-y-3">
                    <h4 className="text-[10px] uppercase tracking-widest text-white flex items-center">
                      <BarChart3 className="h-4 w-4 mr-2" /> Коэффициент вариации цен (п.9 Приказа №45)
                      <InfoTooltip text="Согласно п.9 Порядка: при сборе цен от ≥3 поставщиков рассчитывается коэффициент вариации. Если > 15% — цены неоднородны, нужны дополнительные запросы." />
                    </h4>
                    <p className="text-xs text-white/50 font-mono">
                      Введите коммерческие предложения от поставщиков (минимум 3) для проверки однородности цен.
                    </p>
                    
                    <div className="flex gap-2">
                      <Input 
                        type="number" 
                        placeholder="Цена поставщика (₽)" 
                        value={newSupplierPrice}
                        onChange={(e) => setNewSupplierPrice(e.target.value)}
                        className="rounded-none bg-black border-white/20 text-white font-mono h-10 flex-1"
                      />
                      <Button 
                        type="button"
                        onClick={() => {
                          const price = parseFloat(newSupplierPrice);
                          if (!isNaN(price) && price > 0) {
                            setSupplierPrices(prev => [...prev, price]);
                            setNewSupplierPrice('');
                          }
                        }}
                        className="rounded-none bg-white text-black hover:bg-white/80 h-10 px-4 uppercase tracking-widest text-[10px] font-bold"
                      >
                        + Добавить
                      </Button>
                    </div>
                    
                    {supplierPrices.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex flex-wrap gap-2">
                          {supplierPrices.map((p, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-black border border-white/20 text-xs font-mono text-white">
                              #{i + 1}: {p.toLocaleString('ru-RU')} ₽
                              <button 
                                type="button"
                                onClick={() => setSupplierPrices(prev => prev.filter((_, j) => j !== i))}
                                className="text-white/40 hover:text-white ml-1"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                        
                        {supplierPrices.length >= 3 && (() => {
                          const v = calculateVariation(supplierPrices)!;
                          return (
                            <div className={`p-3 border ${v.homogeneous ? 'border-green-500/30 bg-green-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
                              <div className="grid grid-cols-3 gap-4 text-center">
                                <div>
                                  <div className="text-[10px] uppercase tracking-widest text-white/40">Средняя цена</div>
                                  <div className="text-lg font-mono text-white">{v.mean.toLocaleString('ru-RU', {maximumFractionDigits: 0})} ₽</div>
                                </div>
                                <div>
                                  <div className="text-[10px] uppercase tracking-widest text-white/40">Коэфф. вариации</div>
                                  <div className={`text-lg font-mono ${v.homogeneous ? 'text-green-600' : 'text-red-500'}`}>{v.cv.toFixed(1)}%</div>
                                </div>
                                <div>
                                  <div className="text-[10px] uppercase tracking-widest text-white/40">Статус</div>
                                  <div className={`text-sm font-mono font-bold uppercase ${v.homogeneous ? 'text-green-600' : 'text-red-500'}`}>
                                    {v.homogeneous ? 'Однородны ✓' : 'Неоднородны ✗'}
                                  </div>
                                </div>
                              </div>
                              {!v.homogeneous && (
                                <p className="text-xs text-red-400 mt-2 font-mono">
                                  Коэффициент вариации превышает 15%. Согласно п.9 Приказа №45, рекомендуется направить дополнительные запросы ценовой информации.
                                </p>
                              )}
                            </div>
                          );
                        })()}
                        
                        {supplierPrices.length > 0 && supplierPrices.length < 3 && (
                          <p className="text-xs text-white/40 font-mono">
                            Добавьте минимум 3 цены для расчёта коэффициента вариации (добавлено: {supplierPrices.length}).
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col md:flex-row gap-4 pt-4">
                    <Button type="button" variant="outline" onClick={() => setActiveTab("step2")} className="rounded-none border-white/20 bg-transparent text-white hover:bg-white/10 h-12 md:h-12 md:w-12 p-0 shrink-0 flex items-center justify-center">
                      <ArrowLeft className="h-4 w-4" />
                      <span className="md:hidden ml-2 uppercase tracking-widest text-[10px] font-bold">Назад</span>
                    </Button>
                    <div className="flex flex-col sm:flex-row gap-2 flex-1">
                      <Button
                        type="button"
                        onClick={() => setActiveTab("step4")}
                        className="rounded-none bg-green-500 text-black hover:bg-green-400 uppercase tracking-widest text-[10px] h-12 flex-1 font-bold transition-colors"
                      >
                        <ArrowRight className="mr-2 h-4 w-4" /> Запрос КП у ЧОП
                      </Button>
                      <Button type="button" onClick={handleShare} disabled={shareLoading}
                        className="rounded-none bg-white text-black hover:bg-white/80 uppercase tracking-widest text-[10px] h-12 flex-1 font-bold transition-colors">
                        {shareLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Share2 className="mr-2 h-4 w-4" />}
                        Поделиться
                      </Button>
                      <Button type="button" onClick={handleExportPDF} className="rounded-none bg-red-600 border border-red-600 text-white hover:bg-red-500 uppercase tracking-widest text-[10px] h-12 flex-1 font-bold transition-colors">
                        <Download className="mr-2 h-4 w-4" /> PDF (ЕИС)
                      </Button>
                      <Button type="button" onClick={handleExportHTML} className="rounded-none bg-black border border-green-500 text-green-600 hover:bg-green-500 hover:text-black uppercase tracking-widest text-[10px] h-12 flex-1 font-bold transition-colors">
                        <Download className="mr-2 h-4 w-4" /> HTML Отчет
                      </Button>
                      <Button type="button" onClick={handleExportCSV} className="rounded-none bg-white text-black hover:bg-white/80 uppercase tracking-widest text-[10px] h-12 flex-1 font-bold">
                        <Download className="mr-2 h-4 w-4" /> CSV Таблица
                      </Button>
                    </div>
                  </div>
                  {shareUrl && (
                    <p className="text-xs text-green-600 font-mono mt-2 p-2 bg-green-500/5 border border-green-500/20">
                      Ссылка скопирована: <span className="text-white">{shareUrl}</span>
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-16 text-white/30 flex flex-col items-center">
                  <Calculator className="h-12 w-12 mb-4 opacity-50" />
                  <p className="max-w-[250px] text-xs font-mono uppercase tracking-widest">ДЛЯ ПРОСМОТРА РЕЗУЛЬТАТА ВЫПОЛНИТЕ ШАГ 2</p>
                </div>
              )}
            </TabsContent>

            {/* ШАГ 4: Запрос КП у ЧОП */}
            <TabsContent value="step4" className="bg-black border border-white/10 p-6 mt-4 animate-in fade-in-50">
              {result ? (
                <ChopRequestStep
                  calculationId={`calc_${Date.now()}`}
                  nmckTotal={result.nmtck_total}
                  posts={`${result.posts_count} постов, ${result.total_hours} часов, U=${result.total_u_coef.toFixed(2)}`}
                  period={`${form.watch("start_date")} — ${form.watch("end_date")}`}
                  region={regions.find(r => r.id === form.watch("region_id"))?.name}
                  onBack={() => setActiveTab("step3")}
                />
              ) : (
                <div className="text-center py-16 text-white/30 flex flex-col items-center">
                  <Calculator className="h-12 w-12 mb-4 opacity-50" />
                  <p className="max-w-[250px] text-xs font-mono uppercase tracking-widest">СНАЧАЛА ВЫПОЛНИТЕ РАСЧЁТ НМЦК</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </form>
      </Form>
    </div>
  );
};
